/**
 * React Query hooks, one per endpoint the PWA is allowed to call.
 *
 * Kept separate from `client.ts` so the fetch layer stays testable without a
 * React tree, and so the retry policy lives in exactly one place. The default
 * here is deliberately conservative: **reads may retry, writes never do by
 * default**. The rule that matters most (§ 4.5 — a post is never retried) is
 * enforced where the post mutation is written, in D6; this file sets the ground
 * so that rule is a narrowing of the default rather than an exception to it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { clearPostAttempt, markPostAttempted } from "../offline/draftStore";
import type {
  AuthConfig,
  CountAreas,
  CountList,
  CountSession,
  CountSessions,
  Health,
  LineSync,
  PostSessionRequest,
  PostingStatus,
  LineReveal,
  MovedInBc,
  PendingReceipts,
  SessionOnHand,
  SessionReview,
} from "./types";

export const authConfigQuery = {
  queryKey: ["auth", "config"] as const,
  queryFn: () => apiFetch<AuthConfig>("/auth/config"),
  // The one read that must succeed before anything else can be decided, and the
  // one whose answer never changes while the tab is open: the backend has to
  // restart to change `auth_mode`.
  staleTime: Infinity,
};

export function useAuthConfig() {
  return useQuery(authConfigQuery);
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: () => apiFetch<Health>("/health"),
    // S8 is a diagnostics screen — "which environment am I in" is asked while
    // someone is on the phone, so it should not serve a cached answer.
    staleTime: 0,
    retry: 1,
  });
}

export function useCountAreas(enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas"],
    queryFn: () => apiFetch<CountAreas>("/count/areas"),
    // Structural: a principal's areas come from committed config and change only
    // when `access.yaml` does.
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

// --- Sessions (D2) -----------------------------------------------------------

/**
 * Session URLs are **nested under their area**, exactly as the backend nests
 * them: the area is what tells the backend which BC company to address, and
 * routing every session request through the area means an id from another area is
 * not a way around the area check. The query keys mirror the URLs for the same
 * reason — an invalidation cannot accidentally reach another area's list.
 */
const sessionsPath = (areaId: string) =>
  `/count/areas/${encodeURIComponent(areaId)}/sessions`;

const sessionPath = (areaId: string, sessionId: string) =>
  `${sessionsPath(areaId)}/${encodeURIComponent(sessionId)}`;

export function useAreaSessions(areaId: string, activeOnly: boolean, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", { activeOnly }],
    queryFn: () =>
      apiFetch<CountSessions>(`${sessionsPath(areaId)}?active_only=${activeOnly}`),
    // **Never stale.** BC staging is the store, and this list is how a manager
    // finds a count they may have left open on another iPad ten minutes ago.
    // Serving it from cache is how someone resumes a session that has since been
    // posted by somebody else.
    staleTime: 0,
    enabled,
  });
}

export function useSession(areaId: string, sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId],
    queryFn: () => apiFetch<CountSession>(sessionPath(areaId, sessionId)),
    staleTime: 0,
    enabled,
  });
}

/**
 * Start a count. `retry: 0` is inherited from the client default and matters:
 * a create is not idempotent, and a retried one stages a second session that a
 * manager would then have to notice and cancel.
 *
 * The response is seeded into the single-session cache so the screen it opens
 * does not immediately re-read what this call just returned.
 */
export function useStartSession(areaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postingDate: string) =>
      apiFetch<CountSession>(sessionsPath(areaId), {
        method: "POST",
        body: JSON.stringify({ posting_date: postingDate }),
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(
        ["count", "areas", areaId, "sessions", session.session_id],
        session,
      );
      void queryClient.invalidateQueries({
        queryKey: ["count", "areas", areaId, "sessions"],
      });
    },
  });
}

// --- The count list and entry (D3) -------------------------------------------

export function useCountList(areaId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "items"],
    queryFn: () => apiFetch<CountList>(`/count/areas/${encodeURIComponent(areaId)}/items`),
    // The list is history-seeded and cached server-side already
    // (`count_list_cache_seconds`). Kennedy is 289 items and Ybor Retail 300, so
    // re-fetching it while a manager is typing into it would be both wasteful and
    // disruptive — the list does not change during a count.
    staleTime: 10 * 60 * 1000,
    enabled,
  });
}

/**
 * Set or clear one item's counted quantity.
 *
 * **One request per item, on blur** — not per keystroke, and not a batch on exit.
 * Per keystroke would put a request behind every digit of "1948"; a batch on exit
 * would mean a manager who closes the iPad loses the lot, which is exactly what
 * staging-as-source-of-truth exists to prevent. `PUT …/lines/{item_no}` is
 * idempotent full-state for that item, so a retry is always safe.
 *
 * The response carries the whole updated session, so the session query is
 * **set from it** rather than invalidated: an invalidation would re-read a
 * 289-line session over cooler WiFi after every entry.
 */
export function useSyncLine(areaId: string, sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entry: { item_no: string; counted_qty: string | null }) =>
      apiFetch<LineSync>(
        `${sessionPath(areaId, sessionId)}/lines/${encodeURIComponent(entry.item_no)}`,
        {
          method: "PUT",
          // `counted_qty` goes over the wire as the typed STRING (or null), never
          // as a JS number: `parseFloat("7345.00496")` is a rounding decision
          // nobody asked this app to make. Pydantic parses the string to Decimal.
          body: JSON.stringify({
            item_no: entry.item_no,
            counted_qty: entry.counted_qty,
          }),
        },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(["count", "areas", areaId, "sessions", sessionId], result.session);
    },
  });
}

/**
 * Poll a session's posting outcome.
 *
 * Read-only, and the cadence comes from the server (`next_poll_after_seconds`,
 * 3s backing off to 15s) rather than from a constant here. Polling **stops** at
 * `terminal`: there is nothing further to learn, and a screen that kept asking
 * would keep a manager watching a spinner after the answer had arrived.
 *
 * This is the query the **verifying** state uses (§ 4.5). It is a read, so it is
 * safe to repeat — which is the entire reason verifying can exist: the app can
 * find out what happened without ever re-sending the post.
 */
export function usePostingStatus(areaId: string, sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "posting"],
    queryFn: () => apiFetch<PostingStatus>(`${sessionPath(areaId, sessionId)}/posting`),
    enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data as PostingStatus | undefined;
      if (!data || data.terminal) return false;
      // **The cadence is the server's, and the client has none of its own.**
      // `classify` sends a number with every non-terminal answer (3s backing off
      // to 15s) and null once there is nothing left to wait for, so a missing or
      // non-positive interval means "stop", never "fall back to a number this
      // file made up". A default here would be a second backoff policy, free to
      // disagree with the one the backend and the write-api harness share.
      const seconds = data.next_poll_after_seconds;
      if (seconds === null || seconds <= 0) return false;
      return seconds * 1000;
    },
  });
}

/**
 * The review payload (D5).
 *
 * **Never cached** (§ 4.6): on-hand and unit cost are session-time facts read as of
 * the posting date, so a stale copy is a totals screen that disagrees with what
 * posting is about to do. `staleTime: 0` and no `refetchInterval` — it is read when
 * the screen opens, and re-read when the manager asks.
 */
export function useSessionReview(areaId: string, sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "review"],
    queryFn: () => apiFetch<SessionReview>(`${sessionPath(areaId, sessionId)}/review`),
    enabled,
    staleTime: 0,
    retry: 1,
  });
}

/**
 * Cancel a session — `Open`/`InReview` → `Cancelled` (D-6).
 *
 * A mutation, so it inherits `retry: 0`: cancelling twice is harmless (the backend
 * answers a cancelled session idempotently) but a retry would still be the client
 * deciding to re-send a write, which is not a habit this app has.
 */
export function useCancelSession(areaId: string, sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CountSession>(`${sessionPath(areaId, sessionId)}/cancel`, { method: "POST" }),
    onSuccess: (session) => {
      queryClient.setQueryData(["count", "areas", areaId, "sessions", sessionId], session);
      void queryClient.invalidateQueries({ queryKey: ["count", "areas", areaId, "sessions"] });
    },
  });
}

// --- Submitting and posting (D6) ---------------------------------------------

/**
 * `Open`/`Error` → `InReview` — the transition that makes a count postable.
 *
 * Built at D6 because the S5 → S6 → S7 flow cannot exist without it: `PostSession`
 * refuses anything that is not `InReview`, so a count that was never submitted has
 * no way to reach the confirm at all. It is also the **only way back from a failed
 * post**, which is what S7's `Error` outcome offers.
 *
 * ⚠ It **clears `ErrorText`** on the way through (`ValidateApiTransition`), so every
 * caller must have shown Business Central's words first. That is why the failure
 * card is hoisted to the top of S5 and why the resubmit sits underneath it.
 *
 * A mutation, so `retry: 0` is inherited. Submitting twice is harmless — the second
 * one is refused, `InReview -> InReview` is not a transition — but a client that
 * re-sent writes on its own would be a client that could re-send the post.
 */
export function useSubmitForReview(areaId: string, sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CountSession>(`${sessionPath(areaId, sessionId)}/review`, { method: "POST" }),
    onSuccess: (session) => {
      queryClient.setQueryData(["count", "areas", areaId, "sessions", sessionId], session);
      // The review payload is now about a different status, and the error text it
      // was rendered beside has just been destroyed by this very call.
      void queryClient.invalidateQueries({
        queryKey: ["count", "areas", areaId, "sessions", sessionId, "review"],
      });
      void queryClient.invalidateQueries({ queryKey: ["count", "areas", areaId, "sessions"] });
    },
  });
}

/**
 * **Post the count. The one request in this app that moves inventory, and the one
 * that is never retried at any layer (§ 4.5).**
 *
 * Two properties are the whole of this function:
 *
 * **The marker is written before the request leaves, by this function, so no caller
 * can send the post without it.** Rule 1 of § 4.5. It could have lived in the modal
 * — and then a second call site, one day, would send a post with no marker and the
 * app would have no way to know it might already have posted. `markPostAttempted`
 * upserts for the same reason: a marker that is sometimes skipped is worse than
 * none, because the screen reading it would offer to post again.
 *
 * **A resend is impossible rather than discouraged.** `retry: 0` is inherited from
 * the client default and restated here, because the transport fault breaks the
 * *reply* and not the request: "we sent it and heard nothing" and "it posted" are
 * the same observation from the client, and the only safe response to either is to
 * **ask** Business Central. That asking is `usePostingStatus`, a GET, and the whole
 * verifying state rests on that asymmetry.
 *
 * The response is a `PostingStatusOut` — a starting point, not an outcome (posting
 * happens in a background BC session) — so it seeds the poll's cache and S7 begins
 * from what the trigger already knows.
 */
export function usePostSession(areaId: string, session: CountSession) {
  const queryClient = useQueryClient();
  const sessionId = session.session_id;
  return useMutation({
    retry: 0,
    mutationFn: async (body: PostSessionRequest) => {
      await markPostAttempted(session, areaId);
      return apiFetch<PostingStatus>(`${sessionPath(areaId, sessionId)}/post`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (status) => {
      queryClient.setQueryData(
        ["count", "areas", areaId, "sessions", sessionId, "posting"],
        status,
      );
      // **The marker's question has been answered, so the marker goes.**
      //
      // It means "we sent a post and do not know whether Business Central took it".
      // A clean response settles that: BC took it, and the row now says `Posting`
      // (or `Error`, if validation refused it on the trigger's own round trip).
      // Nothing is lost if the app dies here — those statuses route to S7 by
      // themselves, and S7 polls and offers no way to post. Leaving it set would put
      // a manager on a screen headed *"we don't know yet whether this posted"* about
      // a post whose answer is in their hand.
      void clearPostAttempt(sessionId);
      // Carry the status the trigger just reported into the session cache before
      // re-reading it. Routing is decided from `session.status`, and S7 is reachable
      // only from `Posting`/`Posted` — so without this the navigation to the posting
      // screen races the refetch and can bounce off it back to entry.
      queryClient.setQueryData(
        ["count", "areas", areaId, "sessions", sessionId],
        (previous: CountSession | undefined) =>
          previous ? { ...previous, status: status.status } : previous,
      );
      void queryClient.invalidateQueries({
        queryKey: ["count", "areas", areaId, "sessions", sessionId],
      });
    },
  });
}

/**
 * Live BC on-hand for the entry screen — **Cold Link only** (Copeland
 * 2026-10-29). `enabled` is the area's flag; at any other area this never fires,
 * and the backend would answer 403 if it did.
 *
 * Never cached, never persisted: `gcTime: 0` keeps it out of React Query's cache
 * between mounts, and nothing writes it to IndexedDB ("no money and no on-hand
 * in the draft"). One attempt, no retry — a failure is shown, never papered over.
 */
export function useSessionOnHand(areaId: string, sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "on-hand"],
    queryFn: () => apiFetch<SessionOnHand>(`${sessionPath(areaId, sessionId)}/on-hand`),
    staleTime: 0,
    gcTime: 0,
    retry: 0,
    enabled,
  });
}

/**
 * BC on-hand for ONE saved line — the blind-entry reveal (PHASE_blind-entry-reveal.md).
 *
 * `enabled` is true only once the line is on the server; the server refuses (409)
 * otherwise, so this is a courtesy and not the guard. The key carries the saved
 * quantity and unit so a correction re-reveals on its own. Never cached, never
 * persisted, one attempt: a failure is shown, never papered over.
 */
export function useLineReveal(
  areaId: string,
  sessionId: string,
  itemNo: string,
  savedQty: string,
  savedUnit: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "reveal", itemNo, savedQty, savedUnit],
    queryFn: () =>
      apiFetch<LineReveal>(`${sessionPath(areaId, sessionId)}/lines/${encodeURIComponent(itemNo)}/reveal`),
    staleTime: 0,
    gcTime: 0,
    retry: 0,
    enabled,
  });
}

/** Open PO/transfer documents for the session's items. Live, uncached, never in drafts. */
export function usePendingReceipts(areaId: string, sessionId: string, enabled = true) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "pending-receipts"],
    queryFn: () => apiFetch<PendingReceipts>(`${sessionPath(areaId, sessionId)}/pending-receipts`),
    staleTime: 0,
    gcTime: 0,
    retry: 0,
    enabled,
  });
}

/** Counted lines whose item moved in BC after counting. Review only; a warning. */
export function useMovedInBc(areaId: string, sessionId: string, enabled = true) {
  return useQuery({
    queryKey: ["count", "areas", areaId, "sessions", sessionId, "moved-in-bc"],
    queryFn: () => apiFetch<MovedInBc>(`${sessionPath(areaId, sessionId)}/moved-in-bc`),
    staleTime: 0,
    gcTime: 0,
    retry: 0,
    enabled,
  });
}
